import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
  AddRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CheckCircleOutlineRounded,
  CloseRounded,
  CallSplitRounded,
  DeleteOutline as DeleteOutlineIcon,
  InfoOutlined,
  Inventory2Outlined,
  KeyboardArrowDownRounded,
  LinkRounded,
  SearchRounded,
  TuneRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputBase,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import { FC, memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { UnassignedItem } from 'src/entities/dispatch';
import { PlanningsDeliveryEntity, PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { usePersistedBoolean } from 'src/hooks/usePersistedBoolean';
import { getInfoMetricValue } from 'src/utils/metricHelpers';

import { NotAllocReasonPresenter } from '../V3Plans/presenters/NotAllocReasonPresenter';

import { VehicleRowGroup } from './adapter';
import { DisplayMetric, InfoMetric } from './DisplayMetricToggle';

const INFO_METRIC_KEYS: InfoMetric[] = [
  'origin_dest',
  'section_pref',
  'section_city',
  'address',
  'shipper',
  'time_window',
  'conditions',
];

const INFO_METRIC_LABELS: Record<InfoMetric, string> = {
  origin_dest: '積降地',
  section_pref: '区間(都道府県)',
  section_city: '区間(市区町村)',
  address: '住所',
  shipper: '荷主名',
  time_window: '指定時間',
  conditions: '条件',
};

const integerFormatter = new Intl.NumberFormat('ja-JP');
const decimalFormatter = new Intl.NumberFormat('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* ─── Advanced Filter Types ─── */
type FilterColumn = 'storeName' | 'cases';
type FilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'gt' | 'gte' | 'lt' | 'lte';

interface FilterRow {
  id: string;
  column: FilterColumn;
  operator: FilterOperator;
  value: string;
}

const FILTER_COLUMNS: { value: FilterColumn; label: string }[] = [
  { value: 'storeName', label: '納品先名称' },
  { value: 'cases', label: '数量' },
];

const TEXT_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'contains', label: '...を含む' },
  { value: 'notContains', label: '...を含まない' },
  { value: 'equals', label: '...と等しい' },
  { value: 'notEquals', label: '...と等しくない' },
];

const NUMBER_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'equals', label: '= 等しい' },
  { value: 'notEquals', label: '≠ 等しくない' },
  { value: 'gt', label: '> より大きい' },
  { value: 'gte', label: '≥ 以上' },
  { value: 'lt', label: '< より小さい' },
  { value: 'lte', label: '≤ 以下' },
];

function getOperatorsForColumn(col: FilterColumn) {
  return col === 'cases' ? NUMBER_OPERATORS : TEXT_OPERATORS;
}

function applyFilter(item: UnassignedItem, filter: FilterRow): boolean {
  const { column, operator, value } = filter;
  if (!value.trim()) return true;

  if (column === 'cases') {
    const num = parseFloat(value);
    if (isNaN(num)) return true;
    const itemVal = item.cases;
    switch (operator) {
      case 'equals': return itemVal === num;
      case 'notEquals': return itemVal !== num;
      case 'gt': return itemVal > num;
      case 'gte': return itemVal >= num;
      case 'lt': return itemVal < num;
      case 'lte': return itemVal <= num;
      default: return true;
    }
  }

  const fieldVal = item.storeName.toLowerCase();
  const searchVal = value.toLowerCase();
  switch (operator) {
    case 'contains': return fieldVal.includes(searchVal);
    case 'notContains': return !fieldVal.includes(searchVal);
    case 'equals': return fieldVal === searchVal;
    case 'notEquals': return fieldVal !== searchVal;
    default: return true;
  }
}

let filterIdCounter = 0;
function createFilterRow(): FilterRow {
  return { id: `f-${++filterIdCounter}`, column: 'storeName', operator: 'contains', value: '' };
}

type SortField = 'quantity' | 'weight' | 'volume';
type SortOrder = 'asc' | 'desc';
type StatusLevel = 'ok' | 'critical';

/* ─── DispatchStatusCard ─── */
interface DispatchStatusCardProps {
  /** 当日案件総量 = assigned + unassigned */
  totalCargoCases: number;
  totalCargoWeightKg: number;
  totalCargoVolumeM3: number;
  /** 配車車両の総キャパ。null は集計不能 (該当軸の表示・判定をスキップ) */
  totalCapacityCases: number | null;
  totalCapacityKg: number | null;
  totalCapacityM3: number | null;
  uniqueTruckCount: number;
  totalRotations: number;
  /**
   * 配車不成立 = 当日案件総量 (assigned + unassigned) のいずれか (数量・重量・体積) が
   * トラック総キャパを超えている場合。判定ロジックは親で行い、結果のみを渡す
   */
  isFeasible: boolean;
  /** どのメトリクスを表示するか。displayMetrics に応じて 数量/重量/体積 の値を絞る */
  displayMetrics: Set<DisplayMetric>;
}

/** 数量/重量/体積を displayMetrics に従って表示する。
 *  / は割り算と紛らわしいので使わず、各値を span で並べて gap (CSS) で間隔を確保する。 */
function TriMetricValue({
  metrics,
  cases,
  weightKg,
  volumeM3,
}: {
  metrics: Set<DisplayMetric>;
  cases: number;
  weightKg: number;
  volumeM3: number;
}) {
  const parts: string[] = [];
  if (metrics.has('quantity')) parts.push(`${integerFormatter.format(cases)}cs`);
  if (metrics.has('weight')) parts.push(`${integerFormatter.format(Math.round(weightKg))}kg`);
  if (metrics.has('volume')) parts.push(`${decimalFormatter.format(volumeM3)}m³`);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', columnGap: 1.25 }}>
      {parts.map((p) => (
        <Box component="span" key={p}>{p}</Box>
      ))}
    </Box>
  );
}

const DispatchStatusCard: FC<DispatchStatusCardProps> = ({
  totalCargoCases,
  totalCargoWeightKg,
  totalCargoVolumeM3,
  totalCapacityCases,
  totalCapacityKg,
  totalCapacityM3,
  uniqueTruckCount,
  totalRotations,
  isFeasible,
  displayMetrics,
}) => {
  const [isOpen, setIsOpen] = usePersistedBoolean('convini.statusCard.open', true);
  const level: StatusLevel = isFeasible ? 'ok' : 'critical';
  // 配車可否のみを伝える。未割当件数は本カードでは扱わない (UnassignedStatsCard 側で表示)。
  const headline = level === 'ok' ? '配車可能' : '配車不成立';

  // 不足は displayMetrics に含まれていて、かつ capacity が分かる軸のみ計算する
  const shortageCases = totalCapacityCases != null ? Math.max(0, totalCargoCases - totalCapacityCases) : 0;
  const shortageWeightKg = totalCapacityKg != null ? Math.max(0, totalCargoWeightKg - totalCapacityKg) : 0;
  const shortageVolumeM3 = totalCapacityM3 != null ? Math.max(0, totalCargoVolumeM3 - totalCapacityM3) : 0;
  const shortageParts: string[] = [];
  if (displayMetrics.has('quantity') && shortageCases > 0) {
    shortageParts.push(`${integerFormatter.format(shortageCases)}cs`);
  }
  if (displayMetrics.has('weight') && shortageWeightKg > 0) {
    shortageParts.push(`${integerFormatter.format(Math.round(shortageWeightKg))}kg`);
  }
  if (displayMetrics.has('volume') && shortageVolumeM3 > 0) {
    shortageParts.push(`${decimalFormatter.format(shortageVolumeM3)}m³`);
  }
  const hasShortage = shortageParts.length > 0;

  const levelStyles: Record<StatusLevel, { bg: string; border: string; text: string; iconColor: string }> = {
    ok: {
      bg: 'hsl(var(--muted) / 0.5)',
      border: 'hsl(var(--border))',
      text: 'hsl(var(--muted-foreground))',
      iconColor: 'hsl(var(--muted-foreground))',
    },
    critical: {
      bg: 'hsl(var(--destructive) / 0.05)',
      border: 'hsl(var(--destructive) / 0.3)',
      text: 'hsl(var(--destructive))',
      iconColor: 'hsl(var(--destructive))',
    },
  };
  const s = levelStyles[level];
  const rowSx = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '11px' } as const;
  const valueSx = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'hsl(var(--foreground))' } as const;

  return (
    <Box sx={{ borderRadius: 1.5, border: `1px solid ${s.border}`, bgcolor: s.bg, px: 2, py: 1.5 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setIsOpen((o) => {
          const next = !o;
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
            button_name: '配車可否カード 開閉',
            label: next ? 'OPEN' : 'CLOSE',
          });
          return next;
        })}
        sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Box component="h3" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '-0.01em', color: 'hsl(var(--foreground))', flexShrink: 0, m: 0 }}>本日の配車可否</Box>
          {!isOpen && (
            <Box component="span" sx={{ fontSize: '10px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: s.text }}>
              — {headline}
            </Box>
          )}
        </Box>
        <KeyboardArrowDownRounded sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, color: s.text }}>
          {level === 'ok' ? (
            <CheckCircleOutlineRounded sx={{ fontSize: 14, color: s.iconColor }} />
          ) : (
            <WarningAmberRounded sx={{ fontSize: 14, color: s.iconColor }} />
          )}
          <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>{headline}</Box>
        </Box>

        <Box sx={{ mt: 1.25, '& > * + *': { mt: 0.5 } }}>
          {hasShortage && (
            <Box sx={rowSx}>
              <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>不足</Box>
              <Box component="span" sx={{ ...valueSx, color: s.text, display: 'inline-flex', alignItems: 'baseline', columnGap: 1.25 }}>
                {shortageParts.map((p) => (
                  <Box component="span" key={p}>{p}</Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>総荷物</Box>
            <Box component="span" sx={valueSx}>
              <TriMetricValue
                metrics={displayMetrics}
                cases={totalCargoCases}
                weightKg={totalCargoWeightKg}
                volumeM3={totalCargoVolumeM3}
              />
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>総キャパ</Box>
            <Box component="span" sx={valueSx}>
              <TriMetricValue
                metrics={displayMetrics}
                cases={totalCapacityCases ?? 0}
                weightKg={totalCapacityKg ?? 0}
                volumeM3={totalCapacityM3 ?? 0}
              />
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>使用台数</Box>
            <Box component="span" sx={valueSx}>
              {uniqueTruckCount}台{totalRotations > uniqueTruckCount && `（${totalRotations}便）`}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ─── DispatchSummaryCard (配車サマリ) ─── */
interface DispatchSummaryCardProps {
  /** 配車済 legRun の合計数量 (cs) */
  totalQuantity: number;
  /** 配車済 legRun の合計重量 (kg) */
  totalWeightKg: number;
  /** 配車済 legRun の合計体積 (m³) */
  totalVolumeM3: number;
  /** 配車済車両の合計重量キャパ (kg)。null は不明 */
  totalCapacityKg: number | null;
  /** 配車済車両の合計体積キャパ (m³)。null は不明 */
  totalCapacityM3: number | null;
  /** 売上 (¥)。データなしは 0 */
  totalRevenueYen: number;
  /** 支払 (¥)。データなしは 0 */
  totalPaymentYen: number;
  /** 距離 (km) */
  totalDistanceKm: number;
  /** 時間 (h、小数点 1 桁) */
  totalDurationHours: number;
}

const yenFormatter = new Intl.NumberFormat('ja-JP');

const DispatchSummaryCard: FC<DispatchSummaryCardProps> = ({
  totalQuantity,
  totalWeightKg,
  totalVolumeM3,
  totalCapacityKg,
  totalCapacityM3,
  totalRevenueYen,
  totalPaymentYen,
  totalDistanceKm,
  totalDurationHours,
}) => {
  const [isOpen, setIsOpen] = usePersistedBoolean('convini.summaryCard.open', true);
  const weightRate = totalCapacityKg && totalCapacityKg > 0
    ? Math.round((totalWeightKg / totalCapacityKg) * 100)
    : null;
  const volumeRate = totalCapacityM3 && totalCapacityM3 > 0
    ? Math.round((totalVolumeM3 / totalCapacityM3) * 100)
    : null;
  const profitYen = totalRevenueYen - totalPaymentYen;

  // 折り畳み時の inline summary (サンプル準拠): "1,350cs / 24,819kg / 3,217m³"
  const foldedSummary = [
    `${integerFormatter.format(totalQuantity)}cs`,
    `${integerFormatter.format(Math.round(totalWeightKg))}kg`,
    `${decimalFormatter.format(totalVolumeM3)}m³`,
  ].join(' / ');

  const rowSx = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '11px' } as const;
  const labelSx = { color: 'hsl(var(--muted-foreground))' } as const;
  const valueSx = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'hsl(var(--foreground))' } as const;
  const subSx = { ml: 0.75, fontSize: '10px', fontWeight: 400, color: 'hsl(var(--muted-foreground))' } as const;
  const dividerSx = { my: 0.75, borderTop: '1px solid hsl(var(--border) / 0.5)' } as const;

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid hsl(var(--border))', bgcolor: 'hsl(var(--muted) / 0.5)' }}>
      <Box sx={{ px: 2, py: 1.25 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setIsOpen((o) => {
            const next = !o;
            ReactGA.event('click', {
              screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
              button_name: '配車サマリカード 開閉',
              label: next ? 'OPEN' : 'CLOSE',
            });
            return next;
          })}
          sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box component="h3" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '-0.01em', color: 'hsl(var(--foreground))', flexShrink: 0, m: 0 }}>
              配車サマリ
            </Box>
            {!isOpen && (
              <Box component="span" sx={{ fontSize: '10px', color: 'hsl(var(--muted-foreground))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                — {foldedSummary}
              </Box>
            )}
          </Box>
          <KeyboardArrowDownRounded sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </Box>
      </Box>
      <Collapse in={isOpen}>
        <Box sx={{ px: 2, pb: 1.25, '& > * + *': { mt: 0.5 } }}>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>数量</Box>
            <Box component="span" sx={valueSx}>{integerFormatter.format(totalQuantity)}cs</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>重量</Box>
            <Box component="span" sx={valueSx}>
              {integerFormatter.format(Math.round(totalWeightKg))}kg
              {weightRate != null && (
                <Box component="span" sx={subSx}>積載率 {weightRate}%</Box>
              )}
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>体積</Box>
            <Box component="span" sx={valueSx}>
              {decimalFormatter.format(totalVolumeM3)}m³
              {volumeRate != null && (
                <Box component="span" sx={subSx}>積載率 {volumeRate}%</Box>
              )}
            </Box>
          </Box>

          <Box sx={dividerSx} />
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>売上</Box>
            <Box component="span" sx={valueSx}>¥{yenFormatter.format(totalRevenueYen)}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>支払</Box>
            <Box component="span" sx={valueSx}>¥{yenFormatter.format(totalPaymentYen)}</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>利益</Box>
            <Box component="span" sx={valueSx}>¥{yenFormatter.format(profitYen)}</Box>
          </Box>

          <Box sx={dividerSx} />
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>距離</Box>
            <Box component="span" sx={valueSx}>{integerFormatter.format(Math.round(totalDistanceKm))}km</Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={labelSx}>時間</Box>
            <Box component="span" sx={valueSx}>{decimalFormatter.format(totalDurationHours)}h</Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ─── UnassignedStatsCard ─── */
interface UnassignedStatsCardProps {
  items: UnassignedItem[];
  selectedItems: UnassignedItem[];
}

const UnassignedStatsCard: FC<UnassignedStatsCardProps> = ({ items, selectedItems }) => {
  const [isOpen, setIsOpen] = usePersistedBoolean('convini.unassignedCard.open', true);

  // legRun の実値がある case のみ合算する (null は 0 として扱う)。
  // cases × 係数の推定値は使わない。
  const sumOf = (list: UnassignedItem[]) => ({
    quantity: list.reduce((s, it) => s + (it.cases ?? 0), 0),
    weightKg: list.reduce((s, it) => s + (it.weight ?? 0), 0),
    volumeM3: list.reduce((s, it) => s + (it.volume ?? 0), 0),
  });
  const totals = useMemo(() => sumOf(items), [items]);
  const picked = useMemo(() => sumOf(selectedItems), [selectedItems]);

  const rowSx = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: '11px' } as const;
  const valueSx = { fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'hsl(var(--foreground))' } as const;

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid hsl(var(--border))', bgcolor: 'hsl(var(--muted) / 0.5)' }}>
      <Box sx={{ px: 2, py: 1.25 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setIsOpen((o) => {
            const next = !o;
            ReactGA.event('click', {
              screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
              button_name: '未割当統計カード 開閉',
              label: next ? 'OPEN' : 'CLOSE',
            });
            return next;
          })}
          sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', border: 'none', cursor: 'pointer', p: 0 }}
        >
          <Box component="h3" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '-0.01em', color: 'hsl(var(--foreground))', m: 0 }}>
            未割当（{items.length}件）
          </Box>
          <KeyboardArrowDownRounded sx={{ fontSize: 14, color: 'text.secondary', transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        </Box>
      </Box>
      <Collapse in={isOpen}>
        <Box sx={{ px: 2, pb: 1.25, '& > * + *': { mt: 0.5 } }}>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>数量</Box>
            <Box component="span" sx={valueSx}>
              {integerFormatter.format(picked.quantity)} / {integerFormatter.format(totals.quantity)}cs
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>重量</Box>
            <Box component="span" sx={valueSx}>
              {integerFormatter.format(Math.round(picked.weightKg))} / {integerFormatter.format(Math.round(totals.weightKg))}kg
            </Box>
          </Box>
          <Box sx={rowSx}>
            <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>体積</Box>
            <Box component="span" sx={valueSx}>
              {decimalFormatter.format(picked.volumeM3)} / {decimalFormatter.format(totals.volumeM3)}m³
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

/* ─── DraggableCardItem ─── */
interface DraggableCardItemProps {
  item: UnassignedItem;
  displayMetrics: Set<DisplayMetric>;
  selected: boolean;
  onClick: () => void;
  onShowDetail: (item: UnassignedItem, rect: DOMRect) => void;
  notAllocReasons?: PlanningsNotAllocReason[];
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  onRestoreSplit?: () => void;
}

export const DraggableCardItem: FC<DraggableCardItemProps> = memo(({
  item,
  displayMetrics,
  selected,
  onClick,
  onShowDetail,
  notAllocReasons,
  notAllocDeliveries,
  notAllocTrucks,
  notAllocDrivers,
  onRestoreSplit,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id, data: { unassigned: true } });
  const hasReasons = (notAllocReasons?.length ?? 0) > 0;

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const metrics: string[] = [];
  if (displayMetrics.has('quantity')) {
    metrics.push(item.cases != null ? `${item.cases} cs` : '-- cs');
  }
  if (displayMetrics.has('weight')) {
    // legRun の実値があればそのまま表示 (0 含む)。null なら "--"。
    metrics.push(item.weight != null ? `${integerFormatter.format(Math.round(item.weight))}kg` : '--kg');
  }
  if (displayMetrics.has('volume')) {
    metrics.push(item.volume != null ? `${decimalFormatter.format(item.volume)}m³` : '--m³');
  }

  const activeInfoMetrics = useMemo(
    () => INFO_METRIC_KEYS.filter((k) => displayMetrics.has(k)),
    [displayMetrics],
  );

  const infoText = useMemo(() => {
    if (activeInfoMetrics.length === 0) return '';
    return activeInfoMetrics
      .map((m) => getInfoMetricValue(m, item))
      .filter(Boolean)
      .join(' / ');
  }, [item, activeInfoMetrics]);

  const infoTooltipContent = (
    <Box sx={{ '& > * + *': { mt: 0.25 } }}>
      {activeInfoMetrics.map((m) => {
        const val = getInfoMetricValue(m, item);
        if (!val) return null;
        return (
          <Box component="p" key={m} sx={{ m: 0 }}>
            <Box component="span" sx={{ opacity: 0.7 }}>{INFO_METRIC_LABELS[m]}:</Box>{' '}
            <span>{val}</span>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        borderRadius: 1,
        bgcolor: 'hsl(var(--card))',
        p: 1.5,
        cursor: 'grab',
        userSelect: 'none',
        boxShadow: selected
          ? '0 0 0 2px hsl(var(--primary)), 0 2px 4px hsl(var(--foreground)/0.04)'
          : '0 0 0 1px hsl(var(--border)/0.7), 0 2px 4px hsl(var(--foreground)/0.04)',
        transition: 'box-shadow 0.15s',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Inventory2Outlined sx={{ fontSize: 14, mt: 0.25, flexShrink: 0, color: 'text.secondary' }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500, letterSpacing: '-0.01em', color: 'hsl(var(--card-foreground))' }}>
            {item.storeName}
          </Box>
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', columnGap: 1, fontSize: '11px', fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--muted-foreground))' }}>
            {metrics.map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </Box>
          {infoText && (
            <Tooltip
              title={infoTooltipContent}
              placement="left"
              enterDelay={200}
              componentsProps={{ tooltip: { sx: { maxWidth: 280, fontSize: 11, lineHeight: 1.5 } } }}
            >
              <Box sx={{ mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '9px', color: 'hsl(var(--muted-foreground) / 0.7)' }}>
                {infoText}
              </Box>
            </Tooltip>
          )}
        </Box>
        {item.wasSplit && (
          <Box
            onPointerDown={(e) => e.stopPropagation()}
            sx={{ flexShrink: 0 }}
          >
            <Tooltip title="分割された案件です（クリックで分割を戻す）" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '未割当案件 分割を戻す',
                  });
                  onRestoreSplit?.();
                }}
                aria-label="分割を戻す"
                sx={{ p: 0.25 }}
              >
                <CallSplitRounded sx={{ fontSize: 14, color: 'error.main' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {hasReasons && (
          // NotAllocReasonPresenter 自体が IconButton + Popover をレンダリングする。
          // ドラッグとの干渉を避けるため、pointerDown を止める wrapper で囲む。
          <Box
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ flexShrink: 0 }}
          >
            <NotAllocReasonPresenter
              filteredNotAllocReasons={notAllocReasons ?? []}
              notAllocDeliveries={notAllocDeliveries ?? []}
              notAllocTrucks={notAllocTrucks ?? []}
              notAllocDrivers={notAllocDrivers ?? []}
            />
          </Box>
        )}
        {/* 案件詳細を開く info ボタン (サンプル準拠)。pointerDown を止めてドラッグと干渉させない */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{ flexShrink: 0 }}
        >
          <IconButton
            size="small"
            aria-label="詳細を表示"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '未割当案件 詳細表示',
              });
              const rect = e.currentTarget.getBoundingClientRect();
              onShowDetail(item, rect);
            }}
            sx={{
              p: 0.25,
              color: 'hsl(var(--muted-foreground) / 0.5)',
              '&:hover': { color: 'hsl(var(--foreground))', bgcolor: 'hsl(var(--secondary))' },
            }}
          >
            <InfoOutlined sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
});

/* ─── Main ─── */
type Props = {
  items: UnassignedItem[];
  rowGroups: VehicleRowGroup[];
  /** 配車サマリ計算用 (実重量・売上・距離など)。未指定なら集計しない */
  deliveries?: PlanningsDeliveryWithLegRunEntity[];
  isLoading: boolean;
  displayMetrics: Set<DisplayMetric>;
  selectedItemIds: Set<string>;
  onSelectItem: (id: string) => void;
  /** 案件カード右側の info ボタンクリック時 → 詳細パネルを開く */
  onShowDetail: (item: UnassignedItem, rect: DOMRect) => void;
  /** 自動配車後の未割当理由: order_id (legRunId) → 理由群 */
  notAllocReasonsByOrderId?: Map<number, PlanningsNotAllocReason[]>;
  notAllocDeliveries?: PlanningsDeliveryEntity[];
  notAllocTrucks?: PlanningsTruckEntity[];
  notAllocDrivers?: PlanningsDriverEntity[];
  /** 「固定ルートに対応づけ」ボタンの実装。未指定/fixedRoute 未選択時は disabled */
  onAlignToFixedRoute?: () => void;
  isAligningToFixedRoute?: boolean;
  /** 選択中の未割当案件をまとめて削除する (DELETE /api/v5/shipment_orders/bulk) */
  onDeleteSelected?: () => void;
  /** 分割済みアイコンクリック時に呼ばれる復元コールバック */
  onRestoreSplit?: (item: UnassignedItem) => void;
};

const ConvenienceDispatchUnassignedList: FC<Props> = memo(({
  items,
  rowGroups,
  deliveries,
  isLoading,
  displayMetrics,
  selectedItemIds,
  onSelectItem,
  onShowDetail,
  notAllocReasonsByOrderId,
  notAllocDeliveries,
  notAllocTrucks,
  notAllocDrivers,
  onAlignToFixedRoute,
  isAligningToFixedRoute = false,
  onDeleteSelected,
  onRestoreSplit,
}) => {
  const [searchText, setSearchText] = useState('');
  const [sortField, setSortField] = useState<SortField>('quantity');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [advancedFilters, setAdvancedFilters] = useState<FilterRow[]>([]);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const filterOpen = Boolean(filterAnchor);

  const updateFilter = useCallback((id: string, patch: Partial<FilterRow>) => {
    setAdvancedFilters((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, ...patch };
        if (patch.column && patch.column !== f.column) {
          const ops = getOperatorsForColumn(patch.column);
          if (!ops.find((o) => o.value === updated.operator)) {
            updated.operator = ops[0].value;
          }
        }
        return updated;
      }),
    );
  }, []);

  const activeFilterCount = advancedFilters.filter((f) => f.value.trim()).length;

  // 統計（車両側から算出）
  const realRows = useMemo(() => rowGroups.flatMap((g) => g.rows.filter((r) => !r.source.isPlaceholderRow)), [rowGroups]);
  const uniqueTruckCount = useMemo(() => new Set(realRows.map((r) => r.truckId)).size, [realRows]);
  const totalRotations = realRows.length;

  // 配車済車両 (= delivery を持つトラック) のユニークキャパ合計。
  // 数量・重量・体積どれかでも未設定の車両があれば、その軸は集計不能 (null) とする
  const { totalCapacityCases, totalCapacityKg, totalCapacityM3 } = useMemo(() => {
    const seen = new Set<string>();
    let cases = 0;
    let kg = 0;
    let m3 = 0;
    let casesKnown = true;
    let kgKnown = true;
    let m3Known = true;
    rowGroups.forEach((g) => {
      if (seen.has(g.vehicle.id)) return;
      // 配車実績がある (real row) 車両のみキャパに数える
      const hasReal = g.rows.some((r) => !r.source.isPlaceholderRow);
      if (!hasReal) return;
      seen.add(g.vehicle.id);
      if (g.vehicle.maxCapacity == null) casesKnown = false;
      else cases += g.vehicle.maxCapacity;
      if (g.vehicle.maxCapacityKg == null) kgKnown = false;
      else kg += g.vehicle.maxCapacityKg;
      if (g.vehicle.maxCapacityM3 == null) m3Known = false;
      else m3 += g.vehicle.maxCapacityM3;
    });
    return {
      totalCapacityCases: casesKnown ? cases : null,
      totalCapacityKg: kgKnown ? kg : null,
      totalCapacityM3: m3Known ? m3 : null,
    };
  }, [rowGroups]);

  // 配車サマリの集計 (deliveries ベース、実値で計算)
  // 売上/支払 は shift_truck.tariff_results (billing/payment カテゴリ) を delivery 単位で合算
  // 距離は operations[].drivingDistanceMm 合算 (mm → km)
  // 時間は各 delivery の min(operations.arrivalAt) → max(operations.arrivalAt) を合算 (V3 仕様)
  const summaryTotals = useMemo(() => {
    if (!deliveries || deliveries.length === 0) {
      return {
        quantity: 0,
        weightKg: 0,
        volumeM3: 0,
        revenueYen: 0,
        paymentYen: 0,
        distanceKm: 0,
        durationHours: 0,
      };
    }
    let quantity = 0;
    let weightKg = 0;
    let volumeM3 = 0;
    let revenueYen = 0;
    let paymentYen = 0;
    let distanceMm = 0;
    let durationSeconds = 0;
    for (const d of deliveries) {
      revenueYen += d.tariffBillingYen ?? 0;
      paymentYen += d.tariffPaymentYen ?? 0;
      // 各 delivery 内の operations の最初・最後の arrivalAt の差を勤務時間とする (V3 と同様)
      let minArrivalMs = Infinity;
      let maxArrivalMs = -Infinity;
      for (const op of d.operations) {
        distanceMm += op.drivingDistanceMm ?? 0;
        const t = new Date(op.arrivalAt).getTime();
        if (Number.isFinite(t)) {
          if (t < minArrivalMs) minArrivalMs = t;
          if (t > maxArrivalMs) maxArrivalMs = t;
        }
      }
      if (Number.isFinite(minArrivalMs) && Number.isFinite(maxArrivalMs) && maxArrivalMs > minArrivalMs) {
        durationSeconds += (maxArrivalMs - minArrivalMs) / 1000;
      }
      for (const lr of d.shipmentOrderLegRuns) {
        quantity += lr.item_count ?? 0;
        weightKg += lr.item_total_weight_kg ?? 0;
        volumeM3 += lr.item_total_volume_m3 ?? 0;
      }
    }
    return {
      quantity,
      weightKg,
      volumeM3,
      revenueYen,
      paymentYen,
      distanceKm: distanceMm / 1_000_000,
      durationHours: Math.round((durationSeconds / 3600) * 10) / 10,
    };
  }, [deliveries]);
  const assignedTotal = summaryTotals.quantity;
  const assignedWeightKg = summaryTotals.weightKg;
  const assignedVolumeM3 = summaryTotals.volumeM3;

  // 未割当の合計 (数量・重量・体積)
  const unassignedAggregates = useMemo(() => {
    let cases = 0;
    let weightKg = 0;
    let volumeM3 = 0;
    for (const it of items) {
      cases += it.cases ?? 0;
      weightKg += it.weight ?? 0;
      volumeM3 += it.volume ?? 0;
    }
    return { cases, weightKg, volumeM3 };
  }, [items]);
  const totalCargoCases = assignedTotal + unassignedAggregates.cases;

  // 配車不成立判定: 当日案件総量 (assigned + unassigned) のいずれか (数量・重量・体積) が
  // トラック総キャパを超えていれば不成立。キャパが不明 (null) の軸は判定対象外
  const isFeasible = useMemo(() => {
    const totalCases = assignedTotal + unassignedAggregates.cases;
    const totalWeightKg = assignedWeightKg + unassignedAggregates.weightKg;
    const totalVolumeM3 = assignedVolumeM3 + unassignedAggregates.volumeM3;
    if (totalCapacityCases != null && totalCases > totalCapacityCases) return false;
    if (totalCapacityKg != null && totalWeightKg > totalCapacityKg) return false;
    if (totalCapacityM3 != null && totalVolumeM3 > totalCapacityM3) return false;
    return true;
  }, [assignedTotal, assignedWeightKg, assignedVolumeM3, unassignedAggregates, totalCapacityCases, totalCapacityKg, totalCapacityM3]);

  // 選択された未割当の合計（選択 / 合計 形式で使う）
  const selectedItems = useMemo(
    () => items.filter((it) => selectedItemIds.has(it.id)),
    [items, selectedItemIds],
  );

  const filteredAndSortedItems = useMemo(() => {
    const normalized = searchText.trim().toLowerCase();
    let result = normalized
      ? items.filter((it) => it.storeName.toLowerCase().includes(normalized))
      : [...items];

    if (advancedFilters.length > 0) {
      result = result.filter((item) => advancedFilters.every((f) => applyFilter(item, f)));
    }

    // ソート用の値抽出。実値が無いカード (null) はソート時に最小値扱い (= 0) とする。
    const getValue = (it: UnassignedItem) => {
      switch (sortField) {
        case 'quantity': return it.cases ?? 0;
        case 'weight': return it.weight ?? 0;
        case 'volume': return it.volume ?? 0;
      }
    };

    result.sort((a, b) => {
      const diff = getValue(a) - getValue(b);
      return sortOrder === 'asc' ? diff : -diff;
    });

    return result;
  }, [items, searchText, sortField, sortOrder, advancedFilters]);

  const sortBtnSx = (active: boolean) => ({
    borderRadius: 1,
    px: 1.25,
    py: 0.75,
    fontSize: 12,
    bgcolor: active ? 'hsl(var(--primary) / 0.1)' : 'transparent',
    fontWeight: active ? 600 : 400,
    color: active ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    '&:hover': active ? {} : { bgcolor: 'hsl(var(--secondary))' },
  });

  // 車両 row → 未割当リストへのドロップ受け口。Presenter#handleDragEnd は overId === 'unassigned' で
  // ロウ→未割当の処理を行うので、このパネル全体を droppable として登録する。
  const { setNodeRef: setUnassignedDropRef, isOver: isUnassignedOver } = useDroppable({ id: 'unassigned' });

  return (
    <Stack
      ref={setUnassignedDropRef}
      spacing={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // ドラッグ中にドロップ可能であることを視覚化
        outline: isUnassignedOver ? '2px solid hsl(var(--primary) / 0.5)' : 'none',
        outlineOffset: -2,
        transition: 'outline-color 150ms',
      }}
    >
      {/* Fixed header */}
      <Box sx={{ flexShrink: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <DispatchStatusCard
            totalCargoCases={totalCargoCases}
            totalCargoWeightKg={assignedWeightKg + unassignedAggregates.weightKg}
            totalCargoVolumeM3={assignedVolumeM3 + unassignedAggregates.volumeM3}
            totalCapacityCases={totalCapacityCases}
            totalCapacityKg={totalCapacityKg}
            totalCapacityM3={totalCapacityM3}
            uniqueTruckCount={uniqueTruckCount}
            totalRotations={totalRotations}
            isFeasible={isFeasible}
            displayMetrics={displayMetrics}
          />
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          <DispatchSummaryCard
            totalQuantity={summaryTotals.quantity}
            totalWeightKg={summaryTotals.weightKg}
            totalVolumeM3={summaryTotals.volumeM3}
            totalCapacityKg={totalCapacityKg}
            totalCapacityM3={totalCapacityM3}
            totalRevenueYen={summaryTotals.revenueYen}
            totalPaymentYen={summaryTotals.paymentYen}
            totalDistanceKm={summaryTotals.distanceKm}
            totalDurationHours={summaryTotals.durationHours}
          />
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          <UnassignedStatsCard items={items} selectedItems={selectedItems} />
        </Box>

        {/* 検索 + フィルター */}
        <Box sx={{ px: 2, pt: 0.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ position: 'relative', flex: 1 }}>
            <SearchRounded sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'text.secondary', pointerEvents: 'none' }} />
            <InputBase
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="検索"
              sx={{
                width: '100%',
                height: 32,
                pl: 4,
                pr: 1.5,
                fontSize: 12,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            />
          </Box>
          <Tooltip title="高度なフィルター" placement="top">
            <Box component="span" sx={{ position: 'relative' }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '高度なフィルター',
                  });
                  setFilterAnchor(e.currentTarget);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  border: 1,
                  borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
                  color: activeFilterCount > 0 ? 'primary.main' : 'text.primary',
                  borderRadius: 1,
                }}
              >
                <TuneRounded sx={{ fontSize: 14 }} />
              </IconButton>
              {activeFilterCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute', right: -4, top: -4,
                    display: 'flex', height: 16, width: 16,
                    alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%', bgcolor: 'hsl(var(--primary))',
                    fontSize: '9px', fontWeight: 700, color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          </Tooltip>
          <Popover
            open={filterOpen}
            anchorEl={filterAnchor}
            onClose={() => setFilterAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 420, p: 1.5 } } }}
          >
            <Box sx={{ '& > * + *': { mt: 1.5 } }}>
              {/* Sort */}
              <Box sx={{ '& > * + *': { mt: 0.75 } }}>
                <Box component="p" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 }}>並び替え</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {([['quantity', '数量'], ['weight', '重量'], ['volume', '体積']] as const).map(([value, label]) => (
                    <Box
                      key={value}
                      component="button"
                      type="button"
                      onClick={() => {
                        ReactGA.event('select', {
                          screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                          button_name: '未割当 ソート列',
                          label: value,
                        });
                        setSortField(value);
                      }}
                      sx={sortBtnSx(sortField === value)}
                    >
                      {label}
                    </Box>
                  ))}
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {([['asc', '昇順'], ['desc', '降順']] as const).map(([value, label]) => (
                      <Box
                        key={value}
                        component="button"
                        type="button"
                        onClick={() => {
                          ReactGA.event('select', {
                            screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                            button_name: '未割当 ソート順',
                            label: value === 'asc' ? 'ASC' : 'DESC',
                          });
                          setSortOrder(value);
                        }}
                        sx={{ ...sortBtnSx(sortOrder === value), px: 1 }}
                      >
                        {value === 'asc' ? <ArrowUpwardRounded sx={{ fontSize: 12 }} /> : <ArrowDownwardRounded sx={{ fontSize: 12 }} />}
                        {label}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Advanced filter */}
              <Box sx={{ borderTop: '1px solid hsl(var(--border))', pt: 1, '& > * + *': { mt: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box component="p" sx={{ fontSize: '11px', fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 }}>フィルター</Box>
                  {advancedFilters.length > 0 && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        ReactGA.event('click', {
                          screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                          button_name: 'フィルター 全クリア',
                        });
                        setAdvancedFilters([]);
                      }}
                      sx={{ fontSize: '10px', color: 'hsl(var(--muted-foreground))', bgcolor: 'transparent', border: 'none', cursor: 'pointer', '&:hover': { color: 'hsl(var(--foreground))' } }}
                    >
                      すべてクリア
                    </Box>
                  )}
                </Box>

                {advancedFilters.map((filter) => {
                  const operators = getOperatorsForColumn(filter.column);
                  return (
                    <Box key={filter.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          ReactGA.event('click', {
                            screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                            button_name: 'フィルター行 削除',
                          });
                          setAdvancedFilters((prev) => prev.filter((f) => f.id !== filter.id));
                        }}
                        sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
                        aria-label="フィルターを削除"
                      >
                        <CloseRounded sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Select
                        size="small"
                        value={filter.column}
                        onChange={(e) => {
                          const next = e.target.value;
                          ReactGA.event('select', {
                            screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                            button_name: 'フィルター 列',
                            label: String(next),
                          });
                          updateFilter(filter.id, { column: next });
                        }}
                        sx={{ height: 28, width: 110, fontSize: 11, '& .MuiSelect-select': { py: 0.5 } }}
                      >
                        {FILTER_COLUMNS.map((c) => (
                          <MenuItem key={c.value} value={c.value} sx={{ fontSize: 12 }}>{c.label}</MenuItem>
                        ))}
                      </Select>
                      <Select
                        size="small"
                        value={filter.operator}
                        onChange={(e) => {
                          const next = e.target.value;
                          ReactGA.event('select', {
                            screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                            button_name: 'フィルター 演算子',
                            label: String(next),
                          });
                          updateFilter(filter.id, { operator: next });
                        }}
                        sx={{ height: 28, width: 120, fontSize: 11, '& .MuiSelect-select': { py: 0.5 } }}
                      >
                        {operators.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12 }}>{o.label}</MenuItem>
                        ))}
                      </Select>
                      <InputBase
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                        placeholder="値を入力…"
                        sx={{ height: 28, flex: 1, fontSize: 11, px: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                      />
                    </Box>
                  );
                })}

                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    ReactGA.event('click', {
                      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                      button_name: 'フィルター 追加',
                    });
                    setAdvancedFilters((prev) => [...prev, createFilterRow()]);
                  }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderRadius: 1, px: 1, py: 0.75, fontSize: 12, color: 'hsl(var(--muted-foreground))', bgcolor: 'transparent', border: 'none', cursor: 'pointer', '&:hover': { bgcolor: 'hsl(var(--secondary))', color: 'hsl(var(--foreground))' } }}
                >
                  <AddRounded sx={{ fontSize: 12 }} />
                  フィルターを追加
                </Box>
              </Box>
            </Box>
          </Popover>
        </Box>

        {/* 全選択 + 固定ルートに対応づけ */}
        <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid hsl(var(--border) / 0.4)' }}>
          {/* サンプルコード (route-planner UnassignedPanel) と揃えて、Button で 全選択 / 全解除 をトグル表示する */}
          <Button
            size="small"
            onClick={() => {
              const allSelected = filteredAndSortedItems.length > 0
                && filteredAndSortedItems.every((it) => selectedItemIds.has(it.id));
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '未割当 全選択',
                label: allSelected ? 'OFF' : 'ON',
              });
              filteredAndSortedItems.forEach((it) => {
                const isSel = selectedItemIds.has(it.id);
                // 全選択中なら全解除、そうでなければ不足分を選択
                if (allSelected) {
                  if (isSel) onSelectItem(it.id);
                } else if (!isSel) {
                  onSelectItem(it.id);
                }
              });
            }}
            sx={{ height: 24, px: 1, fontSize: 11, textTransform: 'none', color: 'text.primary', minWidth: 0, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {(filteredAndSortedItems.length > 0 && filteredAndSortedItems.every((it) => selectedItemIds.has(it.id))) ? '全解除' : '全選択'}
          </Button>
          {selectedItemIds.size > 0 && (
            <Box component="span" sx={{ fontSize: '11px', color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {selectedItemIds.size}件選択中
            </Box>
          )}
          {selectedItemIds.size > 0 && onDeleteSelected && (
            <Tooltip title="選択した案件を削除" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  ReactGA.event('click', {
                    screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                    button_name: '未割当案件 削除',
                  });
                  onDeleteSelected();
                }}
                aria-label="選択した案件を削除"
                sx={{ p: 0.25, color: 'hsl(var(--destructive))', flexShrink: 0 }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<LinkRounded sx={{ fontSize: 14 }} />}
            // 固定ルートが読み込まれていないときは disabled（= onAlignToFixedRoute 未指定）
            disabled={!onAlignToFixedRoute || isAligningToFixedRoute}
            // MouseEvent が引数に渡らないよう arrow でラップ
            onClick={() => {
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                button_name: '固定ルートに対応づけ',
              });
              onAlignToFixedRoute?.();
            }}
            sx={{ height: 24, px: 1, fontSize: 11, textTransform: 'none', minWidth: 0, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {isAligningToFixedRoute ? '対応づけ中...' : '固定ルートに対応づけ'}
          </Button>
        </Box>
      </Box>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1.5,
          pb: 1.5,
          pt: 1,
          '& > * + *': { mt: 1 },
        }}
      >
        {isLoading && (
          <Stack spacing={1}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={70} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        )}

        {!isLoading && filteredAndSortedItems.map((item) => {
          const m = /^lr-(\d+)$/.exec(item.id);
          const legRunId = m ? parseInt(m[1], 10) : null;
          const reasons = legRunId != null ? notAllocReasonsByOrderId?.get(legRunId) : undefined;
          return (
            <DraggableCardItem
              key={item.id}
              item={item}
              displayMetrics={displayMetrics}
              selected={selectedItemIds.has(item.id)}
              onClick={() => {
                ReactGA.event('click', {
                  screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
                  button_name: '未割当カード 選択',
                });
                onSelectItem(item.id);
              }}
              onShowDetail={onShowDetail}
              notAllocReasons={reasons}
              notAllocDeliveries={notAllocDeliveries}
              notAllocTrucks={notAllocTrucks}
              notAllocDrivers={notAllocDrivers}
              onRestoreSplit={() => onRestoreSplit?.(item)}
            />
          );
        })}

        {!isLoading && filteredAndSortedItems.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
            {items.length === 0 ? 'すべて割当済み' : '検索条件に一致する未割当はありません'}
          </Box>
        )}
      </Box>
    </Stack>
  );
});

export default ConvenienceDispatchUnassignedList;
